import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { RefreshCw } from 'lucide-react'
import { useMemo, type ReactElement } from 'react'
import { z } from 'zod'

import { DataTablePagination } from '~/components/data/DataTablePagination'
import {
  StripeEventsTable,
  subscriptionsKeys,
  type StripeEvent,
  type StripeEventStatus,
} from '~/features/subscriptions'
import { http } from '~/lib/http'
import {
  toPaginatedResult,
  usePaginatedQuery,
  type PaginatedParams,
  type PaginatedResult,
} from '~/lib/query/use-paginated-query'
import { useUrlState } from '~/lib/query/use-url-state'

/**
 * `/admin/subscriptions/stripe-events` — Read-only event log piped from
 * the stripe-webhooks edge function (URL-paginated).
 *
 * URL search keys: `page`, `limit`, `status`.
 *
 * Manual Refresh button bumps the query; the route does not auto-poll
 * to avoid surprising network usage in admin sessions left open in
 * background tabs.
 */
const stripeEventsSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(10).max(100).default(50),
  status: z
    .enum(['all', 'processed', 'failed', 'replayed', 'skipped'])
    .default('all'),
})
type StripeEventsSearch = z.infer<typeof stripeEventsSearchSchema>

const URL_DEFAULTS: StripeEventsSearch = {
  page: 1,
  limit: 50,
  status: 'all',
}

export const Route = createFileRoute('/admin/subscriptions/stripe-events')({
  validateSearch: stripeEventsSearchSchema,
  component: StripeEventsPage,
})

interface StripeEventsListPayload {
  readonly events?: readonly StripeEvent[]
  readonly items?: readonly StripeEvent[]
  readonly total?: number
  readonly totals?: {
    readonly last_24h: number
    readonly dropped: number
  }
  readonly pagination?: {
    readonly page?: number
    readonly limit?: number
    readonly total?: number
    readonly totalPages?: number
  }
}

interface StripeEventsPage extends PaginatedResult<StripeEvent> {
  readonly totals?: { readonly last_24h: number; readonly dropped: number }
}

async function fetchStripeEventsPage(
  params: PaginatedParams,
): Promise<StripeEventsPage> {
  const query: Record<string, string | number | undefined> = {
    page: params.page,
    limit: params.limit,
  }
  if (params.sort) query.sort = params.sort
  const status = params.filters?.status
  if (status && status !== 'all') query.status = status

  try {
    const raw = await http.get<StripeEventsListPayload>(
      '/api/v1/admin/subscriptions/stripe-events',
      { query },
    )
    const rows = (raw.events ?? raw.items ?? []) as readonly StripeEvent[]
    const total = raw.pagination?.total ?? raw.total ?? rows.length
    const normalized = toPaginatedResult(rows, total, params, raw.pagination)
    return { ...normalized, ...(raw.totals ? { totals: raw.totals } : {}) }
  } catch {
    return toPaginatedResult<StripeEvent>([], 0, params)
  }
}

function StripeEventsPage(): ReactElement {
  const search = Route.useSearch()
  const [, setUrl] = useUrlState(stripeEventsSearchSchema, URL_DEFAULTS)
  const queryClient = useQueryClient()

  const params: PaginatedParams = {
    page: search.page,
    limit: search.limit,
    filters: { status: search.status },
  }

  const events = usePaginatedQuery<StripeEvent>({
    queryKey: [...subscriptionsKeys.all, 'stripe-events-paginated'],
    params,
    fetcher: fetchStripeEventsPage,
    // Stripe events are time-sensitive — match feature default.
    staleTime: 15_000,
  })

  const totals = useMemo(
    () => (events.data as StripeEventsPage | undefined)?.totals,
    [events.data],
  )
  const pagination = events.data?.pagination

  return (
    <div className="ka-subs-list-page">
      <header className="ka-page-header">
        <div>
          <h1 className="ka-page-header-title">Stripe Events</h1>
          <p className="ka-page-header-subtitle">
            {totals
              ? `${totals.last_24h} last 24h · ${totals.dropped} dropped`
              : 'Read-only — webhook ingestion is the canonical write path'}
          </p>
        </div>
        <div className="ka-page-header-actions">
          <button
            type="button"
            className="ka-btn ka-btn--primary"
            onClick={() =>
              void queryClient.invalidateQueries({
                queryKey: subscriptionsKeys.all,
              })
            }
          >
            <RefreshCw size={14} aria-hidden="true" /> Refresh
          </button>
        </div>
      </header>

      <div className="ka-filter-strip" role="search">
        <div className="ka-filter-group" role="group" aria-label="Status">
          {(
            ['all', 'processed', 'failed', 'replayed', 'skipped'] as const
          ).map((s) => (
            <button
              key={s}
              type="button"
              className={`ka-detail-chip${search.status === s ? ' is-active' : ''}`}
              aria-pressed={search.status === s}
              onClick={() =>
                setUrl({
                  status: s as StripeEventStatus | 'all',
                  page: 1,
                })
              }
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <StripeEventsTable
        events={events.data?.data ?? []}
        isLoading={events.isPending}
        error={events.error}
        hasFilters={search.status !== 'all'}
        onClearFilters={() => setUrl({ status: 'all', page: 1 })}
      />
      <DataTablePagination
        className="ka-subs-list-pagination"
        page={pagination?.page ?? search.page}
        limit={pagination?.limit ?? search.limit}
        total={pagination?.total ?? events.data?.data.length ?? 0}
        isLoading={events.isPending}
        onChange={({ page, limit }) => setUrl({ page, limit })}
      />
    </div>
  )
}
